<template>
  <v-list-item
    class="d-flex align-center"
    :class="{ 'account-cell--mobile-typography': $vuetify.breakpoint.smAndDown }"
    link
    @keydown="$emit('keydown-emit', $event)"
    @keydown.enter.prevent="navigateToAccount"
    @keydown.space.prevent="navigateToAccount"
    @focus="$emit('focus', $event)"
    @click="navigateToAccount"
  >
    <v-list-item-avatar
      tile
      color="background2"
      class="rounded-lg"
      style="
        box-shadow: rgba(0, 0, 0, 0.02) 0px 1px 3px 0px,
          rgba(27, 31, 35, 0.15) 0px 0px 0px 1px;
      "
      :style="{
        border: `1px solid ${$vuetify.theme.currentTheme.background3} !important`,
      }"
      :size="$vuetify.breakpoint.smAndDown ? 44 : 50"
    >
      <v-progress-circular
        v-if="logoLoading"
        indeterminate
        size="15"
        width="2"
        color="grey darken-2"
      />
      <v-icon
        v-else-if="!logo?.length"
        :size="$vuetify.breakpoint.smAndDown ? 22 : 25"
      >
        mdi-folder-account
      </v-icon>
      <v-img
        v-else
        contain
        :src="logo"
        :alt="`${$options.filters.capitalize(account.name)} Logo`"
      >
        <template #placeholder>
          <v-row
            class="fill-height ma-0"
            align="center"
            justify="center"
          >
            <v-progress-circular
              indeterminate
              size="15"
              width="2"
              color="grey darken-2"
            />
          </v-row>
        </template>
      </v-img>
    </v-list-item-avatar>
    <v-list-item-content :class="$vuetify.breakpoint.smAndDown ? 'py-1' : 'py-2'">
      <v-list-item-title>
        <span
          :class="[
            'text-capitalize text-wrap font-weight-bold',
            $vuetify.breakpoint.smAndDown ? 'text-body-2' : '',
          ]"
        >{{ account.name }}</span>
      </v-list-item-title>
      <v-list-item-subtitle>
        <span
          :class="[
            'text-capitalize text-wrap font-weight-medium',
            $vuetify.breakpoint.smAndDown
              ? 'text-caption text--secondary'
              : 'text-body-2',
          ]"
        >{{ account.dba }}</span>
      </v-list-item-subtitle>
      <v-list-item-subtitle>
        <span
          :class="[
            'text-capitalize text-wrap',
            $vuetify.breakpoint.smAndDown ? 'text-caption text--secondary' : '',
          ]"
        >{{ account["location.shipping.street.number"] }}
          {{ account["location.shipping.street.name"] }},
          {{ account["location.shipping.city.name"] }},
          {{ account["location.shipping.state.name"] ?? "" }}
          {{ account["location.shipping.zipCode"] }}</span>
      </v-list-item-subtitle>
    </v-list-item-content>
    <v-list-item-action>
      <div
        class="d-flex align-center"
        style="gap: 8px"
      >
        <!-- Desktop: status pill (no icon) -->
        <StatusChip
          v-if="showStatus && accountStatusDef && $vuetify.breakpoint.mdAndUp"
          no-top-margin
          :status="accountStatusDef"
          class="text-capitalize"
        />

        <!-- Quick actions: centered dialog (all breakpoints) -->
        <span
          v-if="showActions"
          @click.stop
          @mousedown.stop
        >
          <v-btn
            icon
            small
            aria-label="Quick actions"
            @click="openActionsMenu"
          >
            <v-icon small>mdi-dots-horizontal</v-icon>
          </v-btn>

          <v-dialog
            :key="`dialog-${account?.objectID}`"
            v-model="actionsMenuOpen"
            max-width="520"
            width="92vw"
            scrollable
            content-class="rounded-lg"
          >
            <v-card class="rounded-lg">
              <v-card-title class="py-2 px-3 d-flex flex-column">
                <div
                  class="d-flex align-center"
                  style="width: 100%"
                >
                  <span class="text-body-2 font-weight-bold">{{
                    account?.name || "Account"
                  }}</span>
                  <v-spacer />
                  <v-btn
                    icon
                    aria-label="Close"
                    @click="actionsMenuOpen = false"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
                <div
                  class="d-flex align-center justify-space-between"
                  style="width: 100%; gap: 8px"
                >
                  <span class="text-caption text--secondary text-truncate">
                    {{ account?.dba || "-" }} |
                    <StatusChip
                      v-if="accountStatusDef"
                      no-top-margin
                      :status="accountStatusDef"
                      class="text-capitalize d-inline-flex align-center"
                    />
                  </span>
                </div>
                <div
                  class="text-caption text--secondary mt-1"
                  style="width: 100%"
                >
                  <span class="text-capitalize">
                    {{ account["location.shipping.street.number"] }}
                    {{ account["location.shipping.street.name"] }},
                    {{ account["location.shipping.city.name"] }},
                    {{ account["location.shipping.state.name"] ?? "" }}
                    {{ account["location.shipping.zipCode"] }}
                  </span>
                </div>
              </v-card-title>
              <v-divider />

              <v-card-text class="py-2 px-2">
                <v-row
                  dense
                  class="ma-0"
                >
                  <v-col
                    cols="6"
                    class="pa-0 pr-1"
                  >
                    <v-menu
                      v-if="orderableBrandIds.length > 1"
                      offset-y
                      left
                      :close-on-content-click="true"
                      content-class="rounded-lg"
                      transition="scale-transition"
                    >
                      <template #activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          small
                          block
                          depressed
                          class="rounded-lg text-none justify-start px-2"
                          :disabled="!canCreateNewOrder"
                          :loading="orderBrandsLoading"
                          v-on="on"
                          @mousedown.stop.prevent
                        >
                          <v-icon
                            small
                            left
                          >mdi-clipboard-plus</v-icon>
                          <span class="text-caption font-weight-bold">New Order</span>
                          <v-icon
                            small
                            right
                            class="ml-auto"
                          >mdi-chevron-down</v-icon>
                        </v-btn>
                      </template>
                      <v-list
                        dense
                        nav
                        class="py-1"
                      >
                        <v-list-item
                          v-for="item in orderBrandsForPicker"
                          :key="item.value"
                          class="rounded-lg"
                          @click="goToNewOrderForBrand(item.value)"
                        >
                          <v-list-item-content>
                            <v-list-item-title class="text-body-2">{{
                              item.text
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <v-btn
                      v-else
                      small
                      block
                      depressed
                      class="rounded-lg text-none justify-start px-2"
                      :disabled="!canCreateNewOrder"
                      @click.stop.prevent="
                        goToNewOrderForBrand(orderableBrandIds[0])
                      "
                      @mousedown.stop.prevent
                    >
                      <v-icon
                        small
                        left
                      >mdi-clipboard-plus</v-icon>
                      <span class="text-caption font-weight-bold">New Order</span>
                    </v-btn>
                  </v-col>
                  <v-col
                    cols="6"
                    class="pa-0 pl-1"
                  >
                    <v-btn
                      small
                      block
                      depressed
                      class="rounded-lg text-none justify-start px-2"
                      :disabled="!hasAvailablePhone(account)"
                      @click.stop.prevent="startCall(account)"
                      @mousedown.stop.prevent
                    >
                      <v-icon
                        small
                        left
                      >mdi-phone</v-icon>
                      <span class="text-caption font-weight-bold">Call</span>
                    </v-btn>
                  </v-col>
                  <v-col
                    cols="6"
                    class="pa-0 pr-1 mt-1"
                  >
                    <v-btn
                      small
                      block
                      depressed
                      class="rounded-lg text-none justify-start px-2"
                      :disabled="!hasAvailableEmail(account)"
                      @click.stop.prevent="sendEmail(account)"
                      @mousedown.stop.prevent
                    >
                      <v-icon
                        small
                        left
                      >mdi-email</v-icon>
                      <span class="text-caption font-weight-bold">Email</span>
                    </v-btn>
                  </v-col>
                  <v-col
                    cols="6"
                    class="pa-0 pl-1 mt-1"
                  >
                    <v-btn
                      small
                      block
                      depressed
                      class="rounded-lg text-none justify-start px-2"
                      @click.stop.prevent="openDirections(account)"
                      @mousedown.stop.prevent
                    >
                      <v-icon
                        small
                        left
                      >mdi-map-marker</v-icon>
                      <span class="text-caption font-weight-bold">Directions</span>
                    </v-btn>
                  </v-col>
                </v-row>

                <div
                  class="d-flex align-center justify-space-between mt-3 px-1"
                >
                  <span
                    class="text-caption font-weight-bold text--secondary text-no-wrap"
                  >
                    Contacts ({{ contacts.length }})
                  </span>
                  <v-btn
                    text
                    x-small
                    class="px-2"
                    @click.stop.prevent="toggleContacts"
                  >
                    <span
                      class="text-caption font-weight-bold text--secondary text-no-wrap"
                    >
                      {{ contactsExpanded ? "Hide" : "Show" }}
                    </span>
                    <v-icon
                      x-small
                      class="ml-1"
                      :style="`transform: rotate(${
                        contactsExpanded ? 180 : 0
                      }deg); transition: transform 0.15s ease;`"
                    >mdi-chevron-down</v-icon>
                  </v-btn>
                </div>
              </v-card-text>

              <v-expand-transition>
                <div v-show="contactsExpanded">
                  <v-divider class="my-0" />
                  <div style="max-height: 45vh; overflow-y: auto">
                    <v-list
                      dense
                      class="py-0"
                    >
                      <v-list-item
                        v-if="contactsLoading"
                        dense
                        class="py-0 px-2"
                      >
                        <v-list-item-content class="py-1">
                          <v-list-item-subtitle class="text--disabled">Loading contacts...</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item
                        v-else-if="contacts.length === 0"
                        dense
                        class="py-0 px-2"
                      >
                        <v-list-item-content class="py-1">
                          <v-list-item-subtitle class="text--disabled">No contacts found.</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <div
                        v-for="(contact, i) in contacts"
                        v-else
                        :key="`dialog-${contact.id}`"
                      >
                        <v-list-item
                          dense
                          class="py-0 px-2"
                          @click.stop="contact.expanded = !contact.expanded"
                        >
                          <v-list-item-content class="py-1">
                            <v-list-item-title
                              class="text-body-2 font-weight-medium text-capitalize text-no-wrap"
                            >
                              {{ contact.fullName }}
                              <span
                                v-if="contact.parent"
                                class="text-caption text--disabled"
                              >
                                (parent)
                              </span>
                            </v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-icon
                              x-small
                              color="grey"
                              :style="`transform: rotate(${
                                contact.expanded ? 180 : 0
                              }deg); transition: transform 0.15s ease;`"
                            >
                              mdi-chevron-down
                            </v-icon>
                          </v-list-item-action>
                        </v-list-item>

                        <v-expand-transition>
                          <div
                            v-show="contact.expanded"
                            class="px-3 pb-2"
                          >
                            <v-row
                              dense
                              class="ma-0"
                            >
                              <v-col
                                cols="6"
                                class="pa-0 pr-1"
                              >
                                <v-btn
                                  small
                                  block
                                  depressed
                                  class="rounded-lg text-none justify-start px-2"
                                  :disabled="!hasAvailablePhone(contact)"
                                  @click.stop.prevent="startCall(contact)"
                                  @mousedown.stop.prevent
                                >
                                  <v-icon
                                    small
                                    left
                                  >mdi-phone</v-icon>
                                  <span class="text-caption font-weight-bold">Call</span>
                                </v-btn>
                              </v-col>
                              <v-col
                                cols="6"
                                class="pa-0 pl-1"
                              >
                                <v-btn
                                  small
                                  block
                                  depressed
                                  class="rounded-lg text-none justify-start px-2"
                                  :disabled="!hasAvailableEmail(contact)"
                                  @click.stop.prevent="sendEmail(contact)"
                                  @mousedown.stop.prevent
                                >
                                  <v-icon
                                    small
                                    left
                                  >mdi-email</v-icon>
                                  <span class="text-caption font-weight-bold">Email</span>
                                </v-btn>
                              </v-col>
                            </v-row>
                          </div>
                        </v-expand-transition>

                        <v-divider
                          v-if="i !== contacts.length - 1"
                          class="my-0"
                        />
                      </div>
                    </v-list>
                  </div>
                </div>
              </v-expand-transition>
            </v-card>
          </v-dialog>
        </span>

        <v-btn
          v-if="showOpenNewTab"
          icon
          small
          aria-label="Open account in new tab"
          @click.stop.prevent="openAccountInNewTab"
          @mousedown.stop.prevent
        >
          <v-icon
            small
            color="review"
          >
            mdi-open-in-new
          </v-icon>
        </v-btn>

        <v-icon
          v-else
          color="grey"
        >
          mdi-chevron-right
        </v-icon>
      </div>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
// Libraries
import { mapGetters } from "vuex";
import {
  firestore,
  getDoc,
  getDocs,
  doc,
  collection,
  query,
  where,
  documentId,
} from "@/firebase/init";

// Components
import StatusChip from "@/components/misc/ui/chips/StatusChip.vue";

export default {
  name: "AccountCell",
  components: {
    StatusChip,
  },
  props: {
    account: {
      type: Object,
      required: true,
    },
    fetchLogo: {
      type: Function,
      required: true,
    },
    showStatus: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showOpenNewTab: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      logoLoaded: false,
      logoLoading: false,
      logo: null,
      actionsMenuOpen: false,
      contactsExpanded: false,
      contactsLoading: false,
      contactsLoaded: false,
      contacts: [],
      accountMetaLoading: false,
      accountMetaLoaded: false,
      accountMeta: {
        brandStatus: null,
        accountStatus: null,
        parent: null,
        phoneNumber: "",
        email: "",
        location: null,
        brands: [],
      },
      orderBrandsForPicker: [],
      orderBrandsLoading: false,
      orderBrandsLoaded: false,
      accountMetaLoadPromise: null,
    };
  },
  watch: {
    account: {
      handler(newAccount, oldAccount) {
        // Only re-fetch if the account ID actually changed
        if (newAccount?.objectID !== oldAccount?.objectID) {
          this.updateAccountLogo(newAccount.objectID);
          // Reset contacts cache when account changes
          this.contacts = [];
          this.contactsLoaded = false;
          this.contactsLoading = false;
          this.contactsExpanded = false;
          // Reset account meta cache
          this.accountMetaLoaded = false;
          this.accountMetaLoading = false;
          this.accountMeta = {
            brandStatus: null,
            accountStatus: null,
            parent: null,
            phoneNumber: "",
            email: "",
            location: null,
            brands: [],
          };
          this.orderBrandsForPicker = [];
          this.orderBrandsLoaded = false;
          this.orderBrandsLoading = false;
          this.accountMetaLoadPromise = null;
          // Reset quick actions menu state
          this.actionsMenuOpen = false;
        }
      },
      immediate: false,
    },
    actionsMenuOpen(val) {
      if (val) {
        void (async () => {
          await this.ensureAccountMetaLoaded();
          await Promise.all([
            this.ensureOrderBrandsForPicker(),
            this.ensureContactsLoaded(),
          ]);
        })();
      } else {
        this.contactsExpanded = false;
      }
    },
  },
  async mounted() {
    this.logoLoading = true;
    this.logo = await this.fetchLogo(this.account.objectID, this.$cacheManager);
    this.logoLoading = false;

    // If BigQuery result didn't include status, fall back to Firestore.
    if (this.showStatus) {
      this.ensureAccountMetaLoaded();
    }
  },
  methods: {
    async updateAccountLogo(accountID) {
      this.logoLoading = true;
      this.logo = null; // Clear the current logo
      this.logo = await this.fetchLogo(accountID, this.$cacheManager);
      this.logoLoading = false;
    },
    navigateToAccount() {
      // Close the menu before navigating
      this.closeMenus();
      this.$router.push({
        name: "accounts-view",
        params: {
          id: this.account.objectID,
        },
      });
    },
    openAccountInNewTab() {
      const id = this.account?.objectID;
      if (!id) return;
      const link = this.$router.resolve({
        name: "accounts-view",
        params: { id },
      });
      window.open(link.href, "_blank", "noopener,noreferrer");
    },
    openActionsMenu(event) {
      // Prevent navigation to account when clicking quick actions
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      // Always reset contacts expansion
      this.contactsExpanded = false;

      // Use Vue.set to ensure reactivity, especially on first click
      this.$set(this, "actionsMenuOpen", true);
    },
    closeMenus() {
      this.actionsMenuOpen = false;
      this.$emit("close");
    },
    async toggleContacts() {
      this.contactsExpanded = !this.contactsExpanded;
      if (this.contactsExpanded) {
        await this.ensureContactsLoaded();
      }
    },
    async ensureAccountMetaLoaded() {
      if (this.accountMetaLoaded) return;
      if (this.accountMetaLoadPromise) {
        return this.accountMetaLoadPromise;
      }
      const accountId = this.account?.objectID;
      if (!accountId) return;

      this.accountMetaLoading = true;
      const accountIdSnapshot = accountId;
      const loadPromise = (async () => {
        try {
          let parent = null;
          let accountStatus = null;
          let phoneNumber = "";
          let email = "";
          let location = null;
          const accountDocSnap = await getDoc(
            doc(firestore, "accounts", accountId),
          );
          let dataBrands = [];
          if (accountDocSnap.exists()) {
            const data = accountDocSnap.data() || {};
            parent = data.parent ?? null;
            accountStatus = data.status ?? null;
            phoneNumber = data.phoneNumber || "";
            email = data.email || "";
            location = data.location || null;
            dataBrands = data.brands?.length ? [...data.brands] : [];
          }

          const propBrands =
            this.account?.brands?.length ? [...this.account.brands] : [];
          const rawForStats = dataBrands.length ? dataBrands : propBrands;

          let brandIdForStats = null;
          const user = this.getUser;
          if (rawForStats.length && user) {
            let candidates = rawForStats;
            if (user.accountType < 4) {
              const allowed = user.brands?.length ? user.brands : [];
              candidates = rawForStats.filter((id) => allowed.includes(id));
            }
            brandIdForStats = candidates[0] ?? null;
          }
          if (!brandIdForStats && user?.brands?.length) {
            brandIdForStats = user.brands[0];
          }

          let brandStatus = null;
          if (brandIdForStats) {
            const brandStatsID = `${accountId}_${brandIdForStats}`;
            const brandStatsSnap = await getDoc(
              doc(firestore, "brandStats", brandStatsID),
            );
            if (brandStatsSnap.exists()) {
              brandStatus = brandStatsSnap.data()?.status ?? null;
            }
          }

          const brandsOnAccount = dataBrands.length
            ? dataBrands
            : propBrands.length
              ? propBrands
              : [];

          if (this.account?.objectID !== accountIdSnapshot) return;

          this.accountMeta = {
            brandStatus,
            accountStatus,
            parent,
            phoneNumber,
            email,
            location,
            brands: brandsOnAccount,
          };
        } catch (e) {
          // ignore; we'll just show no status
        } finally {
          if (this.account?.objectID === accountIdSnapshot) {
            this.accountMetaLoading = false;
            this.accountMetaLoaded = true;
          }
          if (this.accountMetaLoadPromise === loadPromise) {
            this.accountMetaLoadPromise = null;
          }
        }
      })();
      this.accountMetaLoadPromise = loadPromise;

      return loadPromise;
    },
    async ensureContactsLoaded() {
      if (this.contactsLoaded || this.contactsLoading) return;
      this.contactsLoading = true;
      this.contacts = [];

      const accountId = this.account?.objectID;
      if (!accountId) {
        this.contactsLoading = false;
        this.contactsLoaded = true;
        return;
      }

      try {
        // Ensure we know parent/status (used for parent contacts + status badge fallback)
        await this.ensureAccountMetaLoaded();
        const parentId = this.accountMeta?.parent || null;

        const contactsRef = collection(firestore, "contacts");
        const activeQ = where("active", "==", true);
        const accountQ = query(
          contactsRef,
          where("relatedID", "==", accountId),
          activeQ,
        );

        const [accountContactsSnap] = await Promise.all([getDocs(accountQ)]);

        const accountContacts = accountContactsSnap.empty
          ? []
          : accountContactsSnap.docs
              .filter((d) => d.data()?.title !== "customer")
              .map((d) => {
                const data = d.data();
                return {
                  id: d.id,
                  parent: false,
                  fullName: `${data?.name?.first ?? ""} ${
                    data?.name?.last ?? ""
                  }`.trim(),
                  phoneNumber: data?.phoneNumber || "",
                  email: data?.email || "",
                  location: data?.location || null,
                  expanded: false,
                };
              });

        let parentContacts = [];
        if (parentId) {
          const parentQ = query(
            contactsRef,
            where("relatedID", "==", parentId),
            activeQ,
          );
          const parentSnap = await getDocs(parentQ);
          parentContacts = parentSnap.empty
            ? []
            : parentSnap.docs
                .filter((d) => d.data()?.title !== "customer")
                .map((d) => {
                  const data = d.data();
                  return {
                    id: d.id,
                    parent: true,
                    fullName: `${data?.name?.first ?? ""} ${
                      data?.name?.last ?? ""
                    }`.trim(),
                    phoneNumber: data?.phoneNumber || "",
                    email: data?.email || "",
                    location: data?.location || null,
                    expanded: false,
                  };
                });
        }

        this.contacts = [...accountContacts, ...parentContacts].filter(
          (c) => c.fullName.length > 0,
        );
      } catch (e) {
        // fail silently; menu will show empty state
        this.contacts = [];
      } finally {
        this.contactsLoading = false;
        this.contactsLoaded = true;
      }
    },
    hasAvailablePhone(contact) {
      return this.getPhoneValue(contact).length > 0;
    },
    hasAvailableEmail(contact) {
      return this.getEmailValue(contact).length > 0;
    },
    getPhoneValue(contact) {
      if (contact?.phoneNumber?.length) return contact.phoneNumber;
      if (this.account?.phoneNumber?.length) return this.account.phoneNumber;
      if (this.accountMeta?.phoneNumber?.length) return this.accountMeta.phoneNumber;
      return "";
    },
    getEmailValue(contact) {
      if (contact?.email?.length) return contact.email;
      if (this.account?.email?.length) return this.account.email;
      if (this.accountMeta?.email?.length) return this.accountMeta.email;
      return "";
    },
    getLocationValue(contact) {
      if (contact?.location) return contact.location;
      if (this.account?.location) return this.account.location;
      if (this.accountMeta?.location) return this.accountMeta.location;
      return null;
    },
    async startCall(contact) {
      if (!this.accountMetaLoaded && !this.accountMetaLoading) {
        await this.ensureAccountMetaLoaded();
      }

      const phoneNumber = this.getPhoneValue(contact);
      if (!phoneNumber.length) return;

      const location = this.getLocationValue(contact);
      window.open(this.phoneHref(phoneNumber, location?.country));
      this.closeMenus();
    },
    async sendEmail(contact) {
      if (!this.accountMetaLoaded && !this.accountMetaLoading) {
        await this.ensureAccountMetaLoaded();
      }

      const email = this.getEmailValue(contact);
      if (email.length) window.open(`mailto:${email}`);
      this.closeMenus();
    },
    openDirections(contact) {
      const address = [
        `${contact?.["location.shipping.street.number"] ?? ""} ${
          contact?.["location.shipping.street.name"] ?? ""
        }`.trim(),
        contact?.["location.shipping.city.name"] ?? "",
        contact?.["location.shipping.state.name"] ?? "",
        contact?.["location.shipping.zipCode"] ?? "",
      ]
        .filter(Boolean)
        .join(", ");

      const query = encodeURIComponent(address || contact?.name || "");
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${query}`,
        "_blank",
      );
      this.closeMenus();
    },
    goToNewOrderForBrand(brandId) {
      if (!brandId || !this.account?.objectID) return;
      if (!this.orderableBrandIds.includes(brandId)) return;
      this.closeMenus();
      this.$router.push({
        name: "salesOrder-new",
        params: {
          brandStatsID: `${this.account.objectID}_${brandId}`,
        },
      });
    },
    async ensureOrderBrandsForPicker() {
      const ids = this.orderableBrandIds;
      if (ids.length <= 1) return;
      if (this.orderBrandsLoaded || this.orderBrandsLoading) return;

      const accountIdSnapshot = this.account?.objectID;
      this.orderBrandsLoading = true;
      try {
        const brandsRef = collection(firestore, "companyBrands");
        const chunks = [];
        for (let i = 0; i < ids.length; i += 30) {
          chunks.push(ids.slice(i, i + 30));
        }
        const allDocs = [];
        for (const chunk of chunks) {
          if (!chunk.length) continue;
          const q = query(brandsRef, where(documentId(), "in", chunk));
          const response = await getDocs(q);
          if (!response.empty) allDocs.push(...response.docs);
        }
        let options = allDocs
          .map((d) => ({
            text: d.data()?.name ?? d.id,
            value: d.id,
          }))
          .sort((a, b) => (a.text > b.text ? 1 : -1));
        if (!options.length) {
          options = ids.map((id) => ({ text: id, value: id }));
        }
        if (this.account?.objectID === accountIdSnapshot) {
          this.orderBrandsForPicker = options;
        }
      } catch (e) {
        if (this.account?.objectID === accountIdSnapshot) {
          this.orderBrandsForPicker = ids.map((id) => ({ text: id, value: id }));
        }
      } finally {
        this.orderBrandsLoading = false;
        if (this.account?.objectID === accountIdSnapshot) {
          this.orderBrandsLoaded = true;
        }
      }
    },
    /* API */
    // GET
    // POST
    /* */
  },
  computed: {
    ...mapGetters([
      // Main
      "getAppName",
      "getUser",
      "getAccountStatus",
      "getCountryCodes",
    ]),
    orderableBrandIds() {
      const fromMeta =
        this.accountMeta?.brands?.length > 0
          ? [...this.accountMeta.brands]
          : [];
      const fromProp =
        this.account?.brands?.length > 0 ? [...this.account.brands] : [];
      const raw = fromMeta.length ? fromMeta : fromProp;
      if (!raw.length) return [];
      const user = this.getUser;
      if (!user) return [];
      if (user.accountType < 4) {
        const allowed = user.brands?.length ? user.brands : [];
        return raw.filter((id) => allowed.includes(id));
      }
      return raw;
    },
    canCreateNewOrder() {
      return this.orderableBrandIds.length > 0;
    },
    primaryOrderableBrandId() {
      return this.orderableBrandIds[0] ?? null;
    },
    accountStatusDef() {
      const statusValue =
        this.accountMeta?.brandStatus ??
        this.account?.status ??
        this.accountMeta?.accountStatus ??
        null;
      if (!statusValue || !this.getAccountStatus?.length) return null;
      return this.getAccountStatus.find((s) => s.value === statusValue) || null;
    },
  },
};
</script>

<style scoped>
/* Mobile: closer to standard list body/caption scale + comfortable line-height */
.account-cell--mobile-typography >>> .v-list-item__title {
  line-height: 1.35 !important;
}

.account-cell--mobile-typography >>> .v-list-item__subtitle {
  line-height: 1.4 !important;
  margin-top: 2px !important;
}

.account-cell--mobile-typography >>> .v-list-item__subtitle + .v-list-item__subtitle {
  margin-top: 2px !important;
}
</style>
